import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { LucideSparkles } from '@lucide/angular';
import { EthelAccessStore } from '../../store/ethel-access.store';

/** AC-007's fixed keyboard step. Deliberately the SAME 16 px the drawer's resize handle
 *  nudges by (`ethel-drawer.component.ts`, `onResizeKeydown`) — one answer to "how far
 *  does an arrow key move a piece of Ethel chrome", not two. No Shift multiplier: AC-007
 *  asks for a fixed step and nothing asks for a coarse one (`YAGNI`). */
const STEP_PX = 16;

/** Same storage and same key shape as the drawer's `ethel.drawer.width` (AC-011): plain
 *  `localStorage`, best-effort try/catch, clamped on the way in as well as out. Two
 *  preferences still do not justify introducing a storage wrapper the portal does not
 *  have (`YAGNI` — the drawer's own comment made the same call). */
const POSITION_STORAGE_KEY = 'ethel.bubble.position';

const ARROW_STEPS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** Below this much pointer travel (px, Manhattan) the gesture was a shaky click, not a
 *  drag — see `open()` for why the distinction is load-bearing. */
const DRAG_THRESHOLD_PX = 3;

/**
 * design.md §7.6.1 (AC-U6-14) / §7.13 — the entry point. Stays MOUNTED (hidden via CSS,
 * never removed from the DOM) while the drawer is open, so this is the one stable
 * element focus can be restored to on close (`ai-summary-modal`'s focus-restore
 * pattern, applied here to the entry point rather than a triggering row).
 *
 * MOVABLE (FR-002 / AC-006 / AC-007) — pointer drag and Arrow-key nudge, clamped fully
 * inside the viewport, persisted per user in `localStorage`. This lifts the earlier
 * `D-U6-9` deferral ("not draggable — drag belongs to U7, on both surfaces at once"),
 * **for the Employee Portal only and by the user's explicit decision**. The console
 * bubble is still fixed, so the two surfaces deliberately differ until U7 lands there;
 * this note is the record of that, not a TODO to sync them behind anyone's back.
 */
@Component({
  selector: 'app-ethel-bubble',
  standalone: true,
  imports: [TranslocoModule, LucideSparkles],
  templateUrl: './ethel-bubble.component.html',
  styleUrl: './ethel-bubble.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EthelBubbleComponent implements AfterViewInit {
  /** `EthelAccessStore`, not the full `EthelStore` — this component is EAGER (mounted
   *  directly, never inside a `@defer` block), so whatever it injects rides into the
   *  initial bundle. It reads/writes only drawer visibility, which is exactly what the
   *  access store holds (bundle-split follow-up to D-7, gate-decisions.md). */
  protected readonly store = inject(EthelAccessStore);

  @ViewChild('fab') private readonly fabRef?: ElementRef<HTMLButtonElement>;

  /** Viewport-relative top-left of the pill. `null` → no user position yet, and the CSS
   *  `right`/`bottom: var(--ethel-gutter)` anchor stands. */
  protected readonly position = signal<{ x: number; y: number } | null>(null);

  private _wasOpen = false;
  private dragAbort: AbortController | null = null;
  /** True once a pointer drag passed the threshold; consumed by the `click` that follows. */
  private dragged = false;

  constructor() {
    this.restorePosition();

    effect(() => {
      const open = this.store.open();
      if (this._wasOpen && !open) {
        this.fabRef?.nativeElement.focus();
      }
      this._wasOpen = open;
    });

    inject(DestroyRef).onDestroy(() => this.dragAbort?.abort());
  }

  ngAfterViewInit(): void {
    // A restored position can predate a smaller window, or a locale whose label makes the
    // pill wider. Re-clamping through the resize path rather than repeating the maths
    // (`DRY`) — and it needs the rendered element, which only exists from here on.
    this.onViewportResize();
  }

  open(): void {
    // AC-006, second classic break: `pointerup` is followed by a `click`, so without this
    // every reposition would also open the drawer. The flag is reset on the next
    // `pointerdown`, so a drag that ends off the button cannot leave a later genuine click
    // swallowed.
    if (this.dragged) return;
    this.store.openDrawer();
  }

  /** AC-006 — a stored position that no longer fits the window is pulled back in (and
   *  re-persisted, so the correction survives the next reload too). */
  @HostListener('window:resize')
  protected onViewportResize(): void {
    const p = this.position();
    if (!p) return;
    const clamped = this.clamp(p.x, p.y);
    if (clamped.x !== p.x || clamped.y !== p.y) {
      this.position.set(clamped);
      this.persistPosition();
    }
  }

  /** AC-006 — drag. Listeners go on the DOCUMENT, not the button, so the pill keeps
   *  following a pointer that outruns it; `{ signal }` tears all three down in one call
   *  (same shape as the drawer's resize drag, `KISS`). */
  protected onPointerDown(event: PointerEvent): void {
    const el = this.fabRef?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const grabX = event.clientX - rect.left;
    const grabY = event.clientY - rect.top;
    const doc = el.ownerDocument;
    const abort = new AbortController();
    this.dragAbort?.abort();
    this.dragAbort = abort;
    this.dragged = false;

    doc.addEventListener(
      'pointermove',
      (move: PointerEvent) => {
        if (
          Math.abs(move.clientX - event.clientX) + Math.abs(move.clientY - event.clientY) >
          DRAG_THRESHOLD_PX
        ) {
          this.dragged = true;
        }
        this.position.set(this.clamp(move.clientX - grabX, move.clientY - grabY));
      },
      { signal: abort.signal },
    );

    const end = (): void => {
      abort.abort();
      this.dragAbort = null;
      if (this.dragged) this.persistPosition();
    };
    doc.addEventListener('pointerup', end, { signal: abort.signal });
    doc.addEventListener('pointercancel', end, { signal: abort.signal });
  }

  /** AC-007 — Arrow keys while the pill has focus. */
  protected onKeydown(event: KeyboardEvent): void {
    const step = ARROW_STEPS[event.key];
    const el = this.fabRef?.nativeElement;
    if (!step || !el) return;
    // Otherwise the arrow scrolls the page out from under the bubble.
    event.preventDefault();
    // `position()` in preference to the measured rect: key repeat can outrun change
    // detection, and the rect would then still describe the pre-nudge box. The rect is
    // only ever read for the FIRST nudge, to pick up wherever the CSS gutter put it.
    const base = this.position() ?? this.originOf(el);
    this.position.set(this.clamp(base.x + step[0] * STEP_PX, base.y + step[1] * STEP_PX));
    this.persistPosition();
  }

  private originOf(el: HTMLElement): { x: number; y: number } {
    const rect = el.getBoundingClientRect();
    return { x: rect.left, y: rect.top };
  }

  /** AC-006/AC-007 — the WHOLE pill stays inside the viewport, never half off an edge. */
  private clamp(x: number, y: number): { x: number; y: number } {
    const el = this.fabRef?.nativeElement;
    return {
      x: Math.round(Math.max(0, Math.min(x, window.innerWidth - (el?.offsetWidth ?? 0)))),
      y: Math.round(Math.max(0, Math.min(y, window.innerHeight - (el?.offsetHeight ?? 0)))),
    };
  }

  private persistPosition(): void {
    const p = this.position();
    if (!p) return;
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, `${p.x},${p.y}`);
    } catch {
      // Storage unavailable (private mode, quota) — the move still holds for the session.
    }
  }

  private restorePosition(): void {
    try {
      const [x, y] = (localStorage.getItem(POSITION_STORAGE_KEY) ?? '').split(',').map(Number);
      // Not clamped here: the element does not exist yet. `ngAfterViewInit` does it.
      if (Number.isFinite(x) && Number.isFinite(y)) this.position.set({ x, y });
    } catch {
      // Storage unavailable — the default gutter anchor applies.
    }
  }
}
